import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  FormInstance,
  Input,
  Row,
  Skeleton,
  Space,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import VehicleSearchModal from '../VehicleSearchModal';

import './style.less';
import {
  CreateCustomerVehicleRequest,
  CustomerVehicleReduced,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { useDisclosure } from '@/hooks';
import { useCustomerReducedQuery } from '@/core/domain/people/Customers/Customers.hooks';
import {
  DiagnosticFormValues,
  DiagnosticVehicle,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.types';
import { applyCpfCnpjMask, applyMask, removeMask } from '@/utils/mask';
import { cepMask, licensePlateMask, phoneMask } from '@/utils/mask/commonMasks';
import flatContacts from '@/utils/functions/flatContacts';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { CompaniesInfiniteSelectField, InputDate } from '@/components/_common';
import { useCustomerAddresses } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import VehicleModal from '@/components/_common/_VehicleModal';
import { VehicleReturnValues } from '@/components/_common/_VehicleModal/VehicleModal.types';
import { useUpdateCustomerVehicle } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import { useRouteMatch } from 'react-router-dom';
import { queryClient } from '@/Root';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';

const defaultEmptyFieldError = {
  required: true,
  message: <FormattedMessage id="commons.form.errors.requiredField" />,
};

interface Props {
  isVehicleSelectable?: boolean;
  form: FormInstance<DiagnosticFormValues>;
}
const DiagnosticsForm = ({ isVehicleSelectable, form }: Props) => {
  const vehicle: DiagnosticVehicle = Form.useWatch(['vehicle'], form);
  const intl = useIntl();
  const [vehicleSearchVisible, vehicleSearchAction] = useDisclosure();
  const [modalVehicleVisible, modalVehicleAction] = useDisclosure(false);

  const customerVehicleUpdate = useUpdateCustomerVehicle(vehicle?.customerId);

  const {
    params: { diagnosticId },
  } = useRouteMatch<{ diagnosticId: string }>();

  const customerContacts = useCustomerContacts(vehicle?.customerId);
  const customerAddress = useCustomerAddresses(vehicle?.customerId);
  const customerGeneral = useCustomerReducedQuery(vehicle?.customerId);
  const customer = customerGeneral.data;

  const handleVehicleSelected = (selectedVehicle: CustomerVehicleReduced) => {
    form.setFieldsValue({
      vehicle: {
        id: selectedVehicle.id,
        customerId: selectedVehicle.customerId,
        model: selectedVehicle.modelName,
        brand: selectedVehicle.brandName,
        licensePlate: selectedVehicle.licensePlate,
      },
    });
  };

  const onVehicleSave = async (values: VehicleReturnValues) => {
    if (vehicle?.customerId && values) {
      let image = values.image;

      if (!!values.imageUpload) {
        const result = await uploadArchive({
          file: [values.imageUpload as Blob],
          isPublic: false,
        });

        image = result.filePath;
      }

      const obj: CreateCustomerVehicleRequest = {
        model: {
          id: values.modelId,
          brand: { id: values.brandId, image: values.model?.brand.image },
        },
        fuel: { id: values.fuelId },
        color: { id: values.colorId },
        licensePlate: values.licensePlate,
        yearFabrication: Number(values.yearFabrication),
        yearModel: Number(values.yearModel),
        chassi: values.chassi,
        fleet: values.fleet,
        km: values.km,
        renavam: values.renavam,
        type: values?.type,
        active: true,
        accessories: values.accessories || [],
        image,
        cylinders: values.cylinders,
        engineCapacity: values.engineCapacity,
        engineName: values.engineName,
        engineTechnicalCode: values.engineTechnicalCode,
        horsePower: values.horsePower,
        release: values.release,
        valves: values.valves,
        line: values.line,
        airIntake: values.airIntake,
        rearBrake: values.rearBrake,
        frontBrake: values.frontBrake,
        brakeSystem: values.brakeSystem,
        steeringSystem: values.steeringSystem,
        tractionSystem: values.tractionSystem,
        injectionSystem: values.injectionSystem,
        transmissionType: values.transmissionType,
        engineConfiguration: values.engineConfiguration,
      };

      await customerVehicleUpdate.mutateAsync({
        ...obj,
        id: values.vehicleId || vehicle.id,
      });
      await queryClient.invalidateQueries(['diagnostic', Number(diagnosticId)]);
    }
  };

  return (
    <div className="diagnostics-form">
      <FloatingFormItem
        name="companyId"
        label={intl.formatMessage({ id: 'diagnostics.form.company' })}
        rules={[defaultEmptyFieldError]}
      >
        <CompaniesInfiniteSelectField />
      </FloatingFormItem>

      <FloatingFormItem
        name="entryDate"
        label="Data de entrada"
        rules={[defaultEmptyFieldError]}
      >
        <InputDate showTime allowClear={false} />
      </FloatingFormItem>

      <Form.Item
        hidden
        name="vehicle"
        label={intl.formatMessage({ id: 'diagnostics.form.vehicle' })}
        rules={[defaultEmptyFieldError]}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: '12px' }}>
        <Col flex="1">
          <Form.Item shouldUpdate noStyle>
            {({ getFieldError }) => (
              <Card
                bordered
                size="small"
                title={
                  <Space>
                    {intl.formatMessage({ id: 'diagnostics.form.vehicle' })}
                    {!!getFieldError('vehicle')[0] && (
                      <span style={{ color: 'red' }}>
                        {getFieldError('vehicle')[0]}
                      </span>
                    )}
                  </Space>
                }
                extra={
                  <>
                    <Button
                      type="primary"
                      className="flat-search-button"
                      onClick={modalVehicleAction.toggle}
                    >
                      Editar Veículo
                    </Button>

                    {!!isVehicleSelectable && (
                      <Button
                        className="flat-search-button"
                        onClick={vehicleSearchAction.toggle}
                      >
                        <FormattedMessage id="commons.form.button.search" />
                      </Button>
                    )}
                  </>
                }
              >
                <Skeleton
                  loading={
                    customerGeneral.isLoading ||
                    customerAddress.isLoading ||
                    customerContacts.isLoading
                  }
                >
                  {vehicle && customer ? (
                    <Descriptions
                      column={1}
                      style={{ margin: '10px 0px 0px 0px' }}
                    >
                      <Descriptions.Item
                        label={intl.formatMessage({
                          id: 'customers.domain.name',
                        })}
                      >
                        {customer.name}
                      </Descriptions.Item>

                      {!!customer.foreignDocument && (
                        <Descriptions.Item label="Documento estrangeiro">
                          {customer.foreignDocument}
                        </Descriptions.Item>
                      )}

                      {!!customer.cpfCnpj && (
                        <Descriptions.Item
                          label={intl.formatMessage({
                            id: 'customers.domain.cpfCnpj',
                          })}
                        >
                          {applyCpfCnpjMask(customer.cpfCnpj)}
                        </Descriptions.Item>
                      )}

                      <Descriptions.Item
                        label={intl.formatMessage({
                          id: 'commons.contact.contactType.phone',
                        })}
                      >
                        {applyMask(
                          removeMask(
                            flatContacts(customerContacts.data).find(
                              (contact) =>
                                contact.contactType !== ContactType.Email,
                            )?.value,
                          ),
                          phoneMask,
                        ) ?? '-'}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={intl.formatMessage({
                          id: 'commons.contact.contactType.email',
                        })}
                      >
                        {flatContacts(customerContacts.data).find(
                          (contact) =>
                            contact.contactType === ContactType.Email,
                        )?.value ?? '-'}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={intl.formatMessage({
                          id: 'commons.address',
                        })}
                      >
                        {!!customerAddress.data?.data?.length
                          ? `${customerAddress.data?.data[0].street ?? '-'}, ${
                              customerAddress.data?.data[0].number
                            }, ${
                              customerAddress.data?.data[0].district
                            }, ${applyMask(
                              customerAddress.data?.data[0].zipCode,
                              cepMask,
                            )}`
                          : ''}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={intl.formatMessage({
                          id: 'diagnostics.form.vehicle',
                        })}
                      >
                        <>
                          {`${vehicle.model} - ${vehicle.brand} - ${applyMask(
                            vehicle.licensePlate,
                            licensePlateMask,
                          )}`}
                        </>
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Alert
                      showIcon
                      type="warning"
                      message={intl.formatMessage({
                        id: 'diagnostics.form.vehicle.title',
                      })}
                      description={intl.formatMessage({
                        id: 'diagnostics.form.vehicle.description',
                      })}
                    />
                  )}
                </Skeleton>
              </Card>
            )}
          </Form.Item>
        </Col>
      </Row>

      <FloatingFormItem
        name="clientObservation"
        label={intl.formatMessage({ id: 'diagnostics.form.obs' })}
      >
        <Input.TextArea rows={6} />
      </FloatingFormItem>

      <VehicleSearchModal
        visible={vehicleSearchVisible}
        handleVisible={vehicleSearchAction.toggle}
        onVehicleSelected={handleVehicleSelected}
      />

      <VehicleModal
        onOk={onVehicleSave}
        open={modalVehicleVisible}
        onCancel={modalVehicleAction.toggle}
        vehicleId={vehicle?.id}
        customerId={
          vehicle?.customerId ? Number(vehicle?.customerId) : undefined
        }
      />
    </div>
  );
};

export default DiagnosticsForm;
